import { memo } from 'react';

import { Title, VSpacing, useBreakpoint as useMagritteBreakpoint } from '@hh.ru/magritte-ui';
import { SPALink } from '@hh.ru/redux-spa-middleware';
import Column from 'bloko/blocks/column';
import Information from 'bloko/blocks/information';
import BlokoLink from 'bloko/blocks/link';
import Text, { TextImportance } from 'bloko/blocks/text';
import useBreakpoint, { Breakpoint } from 'bloko/common/hooks/useBreakpoint';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import { formatToReactComponent } from 'bloko/common/trl';

import AnnouncementBanner from 'src/components/Banner/AnnouncementBanner';
import NeedHelp from 'src/components/NeedHelp';
import StickySidebar, { StickySidebarAndContent, BLOKO_SCREEN_XS_S_M } from 'src/components/StickySidebar';
import translation from 'src/components/translation';
import { useSelectorNonNullable, useSelector } from 'src/hooks/useSelector';
import { PriceCountryCode } from 'src/models/price/priceCountryCode';
import { UserType } from 'src/models/userType';

import PriceCart from 'src/components/Price/PriceCart';
import PriceCartSummary from 'src/components/Price/PriceCartSummary';
import PriceTabs from 'src/components/Price/PriceTabs';
import RegionSelector from 'src/components/Price/RegionSelector';

const TrlKeys = {
    anyOtherQuestions: 'apple.business.chat.anyOtherQuestions',
    employerIdNotSelected: 'price.employerId.is.not.selected',
    goBack: 'employer.budget',
    noPermission: 'employer.payment.error.noPermissions.BuyServices',
    noPermissionLink: 'employer.payment.error.noPermissions.BuyServices.link',
    serviceNote: 'price-services-note',
};

interface PriceTabContentWrapperProps {
    title?: string;
}

const PriceTabContentWrapper: TranslatedComponent<PriceTabContentWrapperProps> = ({ title, children, trls }) => {
    const breakpoint = useBreakpoint(Breakpoint.M);
    const canBuyServices = useSelectorNonNullable(({ priceData }) => priceData.canBuyServices);
    const priceCountryCode = useSelectorNonNullable(({ priceData }) => priceData.priceCountryCode);
    const priceEmployerId = useSelectorNonNullable(({ priceData }) => priceData.priceEmployerId);
    const isContactUsBlockEnabled = useSelectorNonNullable(({ priceData }) => priceData.isContactUsBlockEnabled);
    const { products } = useSelectorNonNullable(({ priceCart }) => priceCart);
    const hasAnnouncement = useSelector(({ managerAnnouncementBanner }) => managerAnnouncementBanner);
    const userType = useSelector(({ userType }) => userType);
    const employerBudgetLink = useSelector(({ pricePageMetaInfo }) => pricePageMetaInfo.employerBudgetLink);
    const { isMobile } = useMagritteBreakpoint();

    return (
        <>
            <Column xs="4" s="8" m="12" l="16">
                {userType === UserType.BackOffice &&
                    (priceEmployerId ? (
                        <>
                            <BlokoLink disableVisited Element={SPALink} to={employerBudgetLink}>
                                {trls[TrlKeys.goBack]}
                            </BlokoLink>{' '}
                            <Text Element="span" importance={TextImportance.Tertiary}>
                                {priceEmployerId}
                            </Text>
                        </>
                    ) : (
                        <Text Element="span" importance={TextImportance.Tertiary}>
                            {trls[TrlKeys.employerIdNotSelected]}
                        </Text>
                    ))}
                {title ? (
                    <Title Element="h1" size={isMobile ? 'medium' : 'large'}>
                        {title}
                    </Title>
                ) : (
                    <RegionSelector />
                )}
                <VSpacing default={40} />
            </Column>
            {userType === UserType.Employer && !canBuyServices && (
                <>
                    <Column xs="4" s="8" m="12" l="16">
                        <Information>
                            {formatToReactComponent(trls[TrlKeys.noPermission], {
                                '{0}': (
                                    <BlokoLink
                                        Element={SPALink}
                                        to="/employer/managers?hhtmFromLabel=can_not_buy_services"
                                        target="_blank"
                                    >
                                        {trls[TrlKeys.noPermissionLink]}
                                    </BlokoLink>
                                ),
                            })}
                        </Information>
                    </Column>
                    <VSpacing default={40} />
                </>
            )}
            <AnnouncementBanner />
            {hasAnnouncement && <VSpacing default={40} />}
            <Column xs="4" s="8" m="12" l="16">
                <PriceTabs />
            </Column>
            <StickySidebarAndContent>
                <Column xs="4" s="8" m="12" l="12" container>
                    <div>{children}</div>
                    {priceCountryCode === PriceCountryCode.UZ && (
                        <div className="price-services-note">{trls[TrlKeys.serviceNote]}</div>
                    )}
                    {breakpoint !== Breakpoint.L && (
                        <>
                            {products.length !== 0 && <VSpacing default={56} />}
                            <PriceCartSummary />
                        </>
                    )}
                    <div>
                        {isContactUsBlockEnabled && (
                            <Column xs="4" s="8" m="12" l="12">
                                <VSpacing default={40} />
                                <NeedHelp />
                            </Column>
                        )}
                    </div>
                </Column>
                {breakpoint === Breakpoint.L && (
                    <StickySidebar disabledScreens={BLOKO_SCREEN_XS_S_M}>
                        <Column xs="0" s="0" m="0" l="4">
                            <PriceCart />
                        </Column>
                    </StickySidebar>
                )}
            </StickySidebarAndContent>
        </>
    );
};

export default memo(translation(PriceTabContentWrapper));
